import React, { useState, useMemo, useCallback } from 'react';

import { IChartDataContent } from '../../../api/charts/types';
import { useSoulMatrixData } from '../../../hooks/useSoulMatrixData';
import { IProfile } from '../../../types/Profile';
import PersonalContent from '../../PersonalContent';
import TabsContentPanel from '../../TabsContentPanel';
import { sameArray } from '../utils';

import { SOUL_MATRIX_BASE_DATA_CONTENT } from './constants';
import { SoulMatrix } from './SoulMatrix';
import { SoulMatrixTabType } from './types';

interface SoulMatrixContentProps {
  activeProfile: IProfile;
}

export function SoulMatrixContent({ activeProfile }: SoulMatrixContentProps) {
  const [activePositions, setActivePositions] = useState<number[]>([]);

  const { data, isLoading } = useSoulMatrixData(activeProfile);

  const tabs: { key: SoulMatrixTabType; label: string }[] = [
    { key: 'personality_profile', label: 'Профиль личности' },
    { key: 'strengths', label: 'Сильные стороны' },
    { key: 'weaknesses', label: 'Слабые стороны' },
    { key: 'recommendations', label: 'Рекомендации' },
  ];

  const chartContent: IChartDataContent | null = useMemo(() => {
    if (!data) return null;

    return {
      personality_profile: {
        ...SOUL_MATRIX_BASE_DATA_CONTENT.personality_profile,
        personal_qualities: data.personality_profile.personal_qualities,
        soul_comfort: data.personality_profile.soul_comfort,
        self_realization: data.personality_profile.self_realization,
        karmic_debt: data.personality_profile.karmic_debt,
        purpose: data.personality_profile.purpose,
        programs: data.personality_profile.programs,
      },
      strengths: {
        ...SOUL_MATRIX_BASE_DATA_CONTENT.strengths,
        personal_qualities: data.strengths.personal_qualities,
        finances: data.strengths.finances,
        relationships: data.strengths.relationships,
        talents: data.strengths.talents,
        programs: data.strengths.programs,
      },
      weaknesses: {
        ...SOUL_MATRIX_BASE_DATA_CONTENT.weaknesses,
        personal_qualities: data.weaknesses.personal_qualities,
        finances: data.weaknesses.finances,
        relationships: data.weaknesses.relationships,
        programs: data.weaknesses.programs,
      },
      recommendations: {
        ...SOUL_MATRIX_BASE_DATA_CONTENT.recommendations,
        personal_qualities: data.recommendations.personal_qualities,
        finances: data.recommendations.finances,
        relationships: data.recommendations.relationships,
        programs: data.recommendations.programs,
        karmic_tail: data.recommendations.karmic_tail,
      },
    };
  }, [data]);

  const hasData = !!data && chartContent;

  // Stable callback to avoid re-creating function each render
  const handleSelectionChange = useCallback(
    ({ element, elements }: { element?: { positions?: number[] }; elements?: { positions?: number[] }[] }) => {
      const src = element ? [element] : (elements ?? []);
      const next = Array.from(new Set(src.flatMap(el => el.positions ?? []))).sort((a, b) => a - b);

      setActivePositions(prev => (sameArray(prev, next) ? prev : next));
    },
    [],
  );

  return (
    <PersonalContent
      loading={isLoading}
      chart={
        hasData ? (
          <SoulMatrix
            matrix={data.ui_elements.matrix}
            purposeMap={data.ui_elements.purpose}
            healthMap={data.ui_elements.health_map}
            positions={activePositions}
          />
        ) : null
      }
      contentPanel={
        hasData ? (
          <TabsContentPanel
            tabs={tabs}
            chartType={'soulMatrixChart'}
            content={chartContent}
            onSelectionChange={handleSelectionChange}
          />
        ) : null
      }
    />
  );
}
